import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getOsa, getPodklad, oznacDoZpravy, zamknoutVlakno, odemknoutVlakno, OznacDoZpravyData } from './ZasahService';
import { useToast } from '../../../components/toast/useToast';
import { OsaResponse } from '../generated/models/OsaResponse';
import { PodkladResponse } from '../generated/models/PodkladResponse';

const osaQueryKey = (poplachId: number) => ['zasah-osa', poplachId];
const podkladQueryKey = (poplachId: number) => ['zasah-podklad', poplachId];

/**
 * HO-148 Task 4: casova osa zasahu vc. stavu zamku vlakna a `konverzaceId`.
 *
 * `enabled` stejne jako u `usePodklad` nize. Drive hook parametr nemel a volajici
 * ho proto pousteli nepodmineně s komentarem „pravidla hooku" - jenze `enabled`
 * pravidla hooku neporusuje a stejny soubor to o par radku vys resi spravne.
 * Bez nej odchazel `GET /api/poplach/{id}/osa` pri kazdem otevreni detailu
 * udalosti u VSECH jednotek vcetne tech s vypnutym modulem, a u detailu bez id
 * dokonce na `/api/poplach/0/osa`.
 */
export const useOsa = (poplachId: number, enabled = true) =>
    useQuery<OsaResponse, Error>({
        queryKey: osaQueryKey(poplachId),
        queryFn: () => getOsa(poplachId),
        enabled,
    });

/** HO-148 Task 5: textovy podklad pro zpravu o zasahu - pracovni pomucka, ADMIN. */
export const usePodklad = (poplachId: number, enabled = true) =>
    useQuery<PodkladResponse, Error>({
        queryKey: podkladQueryKey(poplachId),
        queryFn: () => getPodklad(poplachId),
        enabled,
    });

export interface OznacDoZpravyVariables extends OznacDoZpravyData {
    typ: 'zprava' | 'priloha';
    entitaId: number;
}

/**
 * HO-148 Task 6: oznaceni/odskrtnuti polozky ("zprava" chatu nebo "priloha"
 * dokumentace) jako podkladu zpravy. Po uspechu se invaliduje osa (meni se
 * `doZpravy`/`kategorie` na polozkach) i podklad (meni se jeho text).
 */
export const useOznacDoZpravy = (poplachId: number) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation<void, Error, OznacDoZpravyVariables>({
        mutationFn: ({ typ, entitaId, doZpravy, kategorie }) =>
            oznacDoZpravy(poplachId, typ, entitaId, { doZpravy, kategorie }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: osaQueryKey(poplachId) });
            queryClient.invalidateQueries({ queryKey: podkladQueryKey(poplachId) });
        },
        // Bez hlasky by zaskrtnuti tise selhalo a uzivatel by videl, jak se
        // zaskrtavatko vrati zpet, aniz by vedel proc. Nejcastejsi duvod je
        // prave 423 `vlakno_uzamceno`. Uspech se nehlasi zamerne - je to
        // zaskrtavatko, ne akce, a toast u kazdeho kliknuti by otravoval.
        onError: (e: Error) => showToast(e.message || 'Označení se nepodařilo uložit', 'error'),
    });
};

/** HO-148 Task 6: rucni uzamceni vlakna po zdokumentovani zasahu - ADMIN. */
export const useZamknoutVlakno = (poplachId: number) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation<void, Error, void>({
        mutationFn: () => zamknoutVlakno(poplachId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: osaQueryKey(poplachId) });
            showToast('Vlákno uzamčeno', 'success');
        },
        onError: (e: Error) => showToast(e.message || 'Vlákno se nepodařilo uzamknout', 'error'),
    });
};

/** HO-148 Task 6: trvale odemkne automaticky/rucne uzamcene vlakno - ADMIN. */
export const useOdemknoutVlakno = (poplachId: number) => {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    return useMutation<void, Error, void>({
        mutationFn: () => odemknoutVlakno(poplachId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: osaQueryKey(poplachId) });
            showToast('Vlákno odemčeno', 'success');
        },
        onError: (e: Error) => showToast(e.message || 'Vlákno se nepodařilo odemknout', 'error'),
    });
};
